"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { Matcher } from "react-day-picker";

import { Calendar } from "./Calendar";
import {
  CalendarGlyph,
  PICKER_PANEL_CLS,
  PICKER_TRIGGER_CLS,
} from "./PickerChrome";
import { fmtYmd, parseYmd, usePopoverAnchor } from "./use-popover-anchor";

/**
 * The office date picker: a hairline field showing the chosen date (mono
 * `YYYY-MM-DD`) that opens a floating {@link Calendar}. Closes
 * on pick / outside-click / Esc — the same popover behavior as SearchSelect
 * (we don't pull in a popover library).
 *
 * Two modes, like the native input it replaces:
 *   • uncontrolled + `name` → emits a hidden input (forms read `YYYY-MM-DD`);
 *   • controlled `value` + `onChange` → no hidden input (the parent composes,
 *     e.g. the reservation form pairs the date with an hour into one field).
 *
 * A span of dates is {@link DateRangePicker}.
 */
export function DatePicker({
  name,
  value: controlled,
  defaultValue,
  onChange,
  ariaLabel,
  placeholder = "select a date…",
  className = "",
  min,
  max,
  required = false,
}: {
  /** When set, a hidden input carries the `YYYY-MM-DD` value for form submit. */
  name?: string;
  /** Controlled value (`YYYY-MM-DD`); pair with `onChange`. */
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  ariaLabel?: string;
  placeholder?: string;
  className?: string;
  /** Earliest selectable date (`YYYY-MM-DD`, inclusive) — earlier days disable. */
  min?: string;
  /** Latest selectable date (`YYYY-MM-DD`, inclusive) — later days disable. */
  max?: string;
  /** A date that can't be cleared: clicking the selected day again keeps it
   *  (by default that click deselects — the way an optional date empties). */
  required?: boolean;
}) {
  const isControlled = controlled !== undefined;
  const [internal, setInternal] = useState(defaultValue ?? "");
  const value = isControlled ? (controlled ?? "") : internal;
  const { open, setOpen, ref, popRef, coords } = usePopoverAnchor(260);

  // Uncontrolled + `name` submits through a hidden input, which a native form
  // reset cannot clear — so follow the surrounding form's reset, or a form
  // that clears itself after a successful add keeps the previous date.
  useEffect(() => {
    if (isControlled || !name) return;
    const form = ref.current?.closest("form");
    if (!form) return;
    const onReset = () => setInternal(defaultValue ?? "");
    form.addEventListener("reset", onReset);
    return () => form.removeEventListener("reset", onReset);
  }, [isControlled, name, defaultValue, ref]);

  const selected = parseYmd(value);
  const minDate = min ? parseYmd(min) : undefined;
  const maxDate = max ? parseYmd(max) : undefined;
  const disabled: Matcher[] = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];
  const set = (v: string) => {
    if (!isControlled) setInternal(v);
    onChange?.(v);
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-empty={!value}
        onClick={() => setOpen((o) => !o)}
        className={PICKER_TRIGGER_CLS}
      >
        <span className="truncate">{value || placeholder}</span>
        <CalendarGlyph />
      </button>

      {name && <input type="hidden" name={name} value={value} />}

      {open &&
        coords &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            style={{ top: coords.top, left: coords.left }}
            className={PICKER_PANEL_CLS}
          >
            <Calendar
              mode="single"
              required={required}
              selected={selected}
              defaultMonth={selected}
              disabled={disabled}
              onSelect={(d: Date | undefined) => {
                set(d ? fmtYmd(d) : "");
                if (d) setOpen(false);
              }}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
